/**
 * Strings for the planner itself: creating a wedding, the workspace, the
 * guest form. Kept apart from the site's dictionary because it is three times
 * longer and changes for different reasons.
 *
 * Every key exists in both languages. A test in the build (see
 * scripts/check-i18n) fails when a key is used by a component but missing
 * here, so a half-translated screen cannot ship by accident.
 */

export const PLANNER = {
  en: {
    // --- creating a wedding ------------------------------------------------
    "create.back": "Back",
    "create.step": "Step one",
    "create.title": "Set the room",
    "create.intro":
      "Tables and seats first. You will get a link to share with your guests, and their answers will fill this in as they arrive.",
    "create.couple": "The couple",
    "create.couplePlaceholder": "Amélie & Julien",
    "create.email": "Your email",
    "create.emailHint":
      "Replies to the invitation come back to you here. Placy needs it to send the invitations in your name.",
    "create.language": "Language of the wedding",
    "create.languageHint": "Invitations and the printed plan are written in it.",
    "create.password": "Password",
    "create.passwordHint":
      "Protects your guest list — their diets, and who they asked not to sit with. Your guests never need it: the form link stays open.",
    "create.passwordPlaceholder": "At least 8 characters",
    "create.venue": "Venue",
    "create.venuePlaceholder": "Domaine de la Roselière",
    "create.date": "Date",
    "create.pickDate": "Pick a date",
    "create.tables": "Tables",
    "create.seats": "Seats per table",
    "create.seatsWord": "seats",
    "create.less": "Fewer",
    "create.more": "More",
    "create.submit": "Create wedding",
    "create.orDemo": "or try the demo wedding first",
    "create.whose": "Whose wedding is it?",
    "create.shortPassword": "Pick a password of at least 8 characters",
    "create.badEmail": "That email address does not look right",
    "create.created": "Wedding created — now invite your guests",
    "create.failed": "Could not create the wedding",

    // --- the workspace shell -------------------------------------------------
    "nav.invitations": "Invitations",
    "admin.opening": "Opening your workspace",
    "admin.private": "This wedding is private",
    "admin.privateHint":
      "The list holds names, diets and the avoid requests. Enter the password to open it.",
    "admin.passwordPlaceholder": "Wedding password",
    "admin.open": "Open the plan",
    "admin.guestsNoNeed": "Your guests never need it: the form link stays open.",
    "admin.wrongPassword": "Wrong password",
    "admin.notFound": "We cannot find this wedding",
    "admin.notFoundHint": "Check the link, or create a new one.",
    "admin.home": "Back to home",
    "admin.demo": "demo wedding",
    "admin.refreshNotes": "Refresh {n} AI note(s)",
    "admin.working": "Working",
    "admin.seating": "Seating {n} guests",
    "admin.seatingHint":
      "Reading the answers, scoring affinities, then placing by code. Usually under a minute.",
    "admin.noPlan": "No plan yet",
    "admin.readyTitle": "Everything is ready for seating",
    "admin.startTitle": "Start by adding your guests",
    "admin.readyHint":
      "{guests} guests, {answered} answers so far. The plan works even when most people have not replied.",
    "admin.startHint": "Use the Guests tab to add your invite list, then share the form link.",
    "admin.generateCta": "Generate the seating plan",
    "admin.manageGuests": "Manage guests",
    "admin.planReady": "Plan ready in {s} s",
    "admin.planReadyDesc": "{seated} guests seated · {conflicts} conflicts",
    "admin.generationFailed": "Generation failed",
    "admin.building": "Building the plan",
    "admin.buildingDesc": "Usually under a minute.",
    "admin.cannotStart": "Could not start generation",
    "admin.conflicts": "{n} avoid conflict(s) in the plan",
    "admin.conflictsDesc": "See the validation panel to fix.",
    "admin.overCapacity": "Table {t} is over capacity",
    "admin.cannotMove": "Could not move this guest",
    "admin.locked": "Table {t} locked — regenerating keeps it as it is",
    "admin.unlocked": "Table {t} unlocked",
    "admin.cannotLock": "Could not lock this table",
    "admin.pinned": "{name} stays at table {t} when you regenerate",
    "admin.unpinned": "{name} unpinned",
    "admin.cannotPin": "Could not pin this guest",
    "admin.renamed": "Table renamed “{title}”",
    "admin.nameRestored": "Generated name restored",
    "admin.cannotRename": "Could not rename this table",
    "admin.notesRewritten": "{n} table note(s) rewritten",
    "admin.notesFresh": "Notes are already up to date",
    "admin.cannotRefresh": "Could not refresh the notes",
    "admin.guest": "Guest",
    "admin.rebuildRest": "Rebuild the rest of the room?",
    "admin.startOver": "Start over?",
    "admin.movedByHand": "You moved guests by hand.",
    "admin.keptLocked": "{n} locked table(s)",
    "admin.keptPinned": "{n} pinned guest(s)",
    "admin.and": "and",
    "admin.willBeKept":
      "will be kept exactly as they are; every other manual change will be lost.",
    "admin.regenerateLoses":
      "Regenerating builds a brand new plan and those manual changes will be lost. Lock a table or pin a guest first to keep the parts you like.",
    "admin.keepPlan": "Keep my plan",
    "admin.regenerate": "Regenerate",

    // --- overview --------------------------------------------------------------
    "overview.guestsInvited": "Guests invited",
    "overview.answeredForm": "Answered the form",
    "overview.ofList": "{p}% of the list",
    "overview.seats": "Seats",
    "overview.tablesTimes": "{n} tables × {s}",
    "overview.plan": "Plan",
    "overview.ready": "Ready",
    "overview.notYet": "Not yet",
    "overview.generateWhen": "Generate when you like",
    "overview.share": "Share",
    "overview.twoLinks": "Two links, no accounts",
    "overview.guestForm": "Guest form (public)",
    "overview.guestFormHint": "Send this to your guests. Two minutes to fill in.",
    "overview.workspace": "Your private workspace",
    "overview.workspaceHint": "Keep this one to yourself — anyone with it can edit the plan.",
    "overview.contact": "Your email",
    "overview.contactHint":
      "Where guests' replies to the invitation land. Placy sends the invitations in your name and needs it.",
    "overview.contactSave": "Save address",
    "overview.contactSaved": "Address saved",
    "overview.invitation": "Invitation",
    "overview.invitationHint":
      "Written for you, with the link inside. Send it from Placy in the Invitations tab, or copy it and send it from your own mailbox.",
    "overview.subject": "Subject",
    "overview.copy": "Copy",
    "overview.copied": "Invitation copied",
    "overview.openMail": "Open in mail app",
    "overview.loadingMessage": "Loading the message…",
    "overview.room": "The room",
    "overview.tablesSeats": "Tables & seats",
    "overview.tables": "Tables",
    "overview.seatsPerTable": "Seats per table",
    "overview.seatsFor": "{total} seats for {n} guests",
    "overview.notEnough": " — not enough room",
    "overview.seatsEach": "Seats at each table",
    "overview.seatsEachHint": "A head table rarely holds the same number as the rest of the room.",
    "overview.table": "Table",
    "overview.headTable": "Head table",
    "overview.headTableHint":
      "Guests marked couple, parent or witness in the Guests tab are seated here.",
    "overview.noHeadTable": "No head table",
    "overview.kidsTable": "Children's table",
    "overview.kidsHint":
      "Children from the chosen age are seated there. Below it, they stay with their parents — and the couple is never split.",
    "overview.noKidsTable": "No children's table",
    "overview.kidsFrom": "from",
    "overview.kidsYears": "years",
    "overview.kidsTitle": "Below this age, a child stays with their parents",
    "overview.feel": "How the room should feel",
    "overview.feelHint": "Applied when you generate. Avoid requests are never affected by these.",
    "overview.keepFamilies": "Keep families together",
    "overview.mixFamilies": "Mix families",
    "overview.keepSides": "Keep each side apart",
    "overview.mixSides": "Mix both sides",
    "overview.saveRoom": "Save room",
    "overview.roomUpdated": "Room updated — regenerate to reseat everyone",
    "overview.roomFailed": "Could not save the room",
    "overview.yourPlan": "Your plan",
    "overview.nextStep": "Next step",
    "overview.seatedAcross": "{seated} guests seated across {n} tables",
    "overview.everyAvoid": "Every avoid request honoured",
    "overview.avoidToResolve": "{n} avoid conflict(s) to resolve",
    "overview.wishesMet": "{a}/{b} sit-near wishes met",
    "overview.toDecide": "{n} to decide",
    "overview.kitchen": "Kitchen summary",
    "overview.placementInCode": "placement in code, {n} improvements",
    "overview.generatedIn": "generated in {s}s",
    "overview.aiCalls": "{n} AI calls in {s}s",
    "overview.retried": "{n} retried",
    "overview.fellBack": "{n} fell back to code",
    "overview.embeddings": "embeddings",
    "overview.grouping": "grouping",
    "overview.byAi": "by AI",
    "overview.byCode": "by code",
    "overview.pinsKept": "{n} guests kept where you locked them",
    "overview.openPlan": "Open the seating plan",
    "overview.exportPdf": "Export PDF",
    "overview.readyWhen": "Ready when you are",
    "overview.addFirst":
      "Add your guest list first, then share the form link. You can generate a plan even when most people have not answered.",
    "overview.partial":
      "{guests} guests on the list, {answered} have answered. You can generate now — the plan works with partial answers.",
    "overview.generatePlan": "Generate seating plan",

    // --- guests ------------------------------------------------------------------
    "guests.all": "All",
    "guests.answered": "Answered",
    "guests.pending": "No answer yet",
    "guests.imported": "{added} added, {updated} updated",
    "guests.cannotRead": "Could not read that file",
    "guests.cannotApply": "Could not apply that change",
    "guests.firstNameNeeded": "A first name is needed",
    "guests.added": "Guest added",
    "guests.cannotAdd": "Could not add the guest",
    "guests.updated": "Guest updated",
    "guests.cannotUpdate": "Could not update the guest",
    "guests.removed": "{name} removed",
    "guests.cannotRemove": "Could not remove the guest",
    "guests.spreadsheet": "Spreadsheet",
    "guests.workFromExcel": "Work from Excel",
    "guests.excelHint":
      "Download the list, edit it in Excel with your wedding planner, upload it back. Rows keep their id so an upload updates people instead of duplicating them — leave the id empty to add someone new.",
    "guests.template": "Blank template",
    "guests.export": "Export {n} guests",
    "guests.upload": "Upload a list",
    "guests.importReport": "{added} added · {updated} updated",
    "guests.children": "{n} children",
    "guests.inviteList": "Invite list",
    "guests.addGuest": "Add a guest",
    "guests.addHint":
      "Households keep people together: give a couple or a family the same household name. A role seats someone at the head table. Guests fill in the rest themselves at",
    "guests.firstName": "First name",
    "guests.lastName": "Last name",
    "guests.email": "Email",
    "guests.emailHint": "for their personal invitation",
    "guests.side": "Side",
    "guests.household": "Household",
    "guests.householdPlaceholder": "marin",
    "guests.generation": "Generation",
    "guests.add": "Add",
    "guests.count": "{n} guests",
    "guests.countAnswered": "({n} answered)",
    "guests.search": "Search",
    "guests.selected": "{n} selected",
    "guests.setSide": "Set side",
    "guests.setRole": "Set role",
    "guests.noRole": "No role",
    "guests.householdName": "Household name",
    "guests.delete": "Delete",
    "guests.clear": "Clear",
    "guests.selectAll": "Select all",
    "guests.shown": "{n} shown",
    "guests.movedTo": "{n} moved to {side}",
    "guests.backToOrdinary": "{n} back to ordinary guests",
    "guests.markedAs": "{n} marked as {role}",
    "guests.groupedAs": "{n} grouped as “{name}”",
    "guests.ungrouped": "{n} ungrouped",
    "guests.householdLabel": "household",
    "guests.answeredBadge": "Answered",
    "guests.noAnswer": "No answer",
    "guests.edit": "Edit {name}",
    "guests.remove": "Remove {name}",
    "guests.select": "Select {name}",
    "guests.comingWith": "Coming with",
    "guests.dietary": "Dietary",
    "guests.ownWords": "In their own words",
    "guests.wantsNear": "Wants to sit near",
    "guests.mustNot": "Must not sit with",
    "guests.noMatch": "No guest matches that search.",
    "guests.none": "No guests yet. Add the first one above.",
    "guests.removeTitle": "Remove {n} guest(s)?",
    "guests.removeHint":
      "Their answers go with them — diets, seating wishes, and who they asked not to sit with. Any children they brought are removed too. This cannot be undone, so export the list first if you are unsure.",
    "guests.keepThem": "Keep them",
    "guests.removeConfirm": "Remove",
    "guests.removedCount": "{n} removed",
    "guests.removedWithChildren": "{n} removed, including {c} children",

    // --- invitations ---------------------------------------------------------------
    "invitations.cannotLoad": "Could not load the invitations",
    "invitations.noEmailSelection": "No guest with an address in this selection.",
    "invitations.rowsCopied": "{n} row(s) copied — paste into a spreadsheet",
    "invitations.copyFailed": "Copying is not possible from this browser.",
    "invitations.loading": "Loading…",
    "invitations.guests": "Guests",
    "invitations.withEmail": "With an address",
    "invitations.answered": "Answered",
    "invitations.silent": "No answer",
    "invitations.missingAddresses":
      "{n} guest(s) have no address, {m} of whom have not answered yet — you cannot chase them by email. Add their address in the spreadsheet (Email column of the Excel template), or pass their link on another way.",
    "invitations.all": "All",
    "invitations.filterSilent": "No answer",
    "invitations.filterAnswered": "Answered",
    "invitations.noEmail": "No address",
    "invitations.search": "Search",
    "invitations.copySelection": "Copy selection",
    "invitations.hasAnswered": "Answered",
    "invitations.notAnswered": "No answer",
    "invitations.noAddress": "no address",
    "invitations.openAsGuest": "Open the form as this guest will see it",
    "invitations.copyLink": "Copy their personal link",
    "invitations.nobody": "Nobody in this selection.",
    "invitations.personalHint":
      "Each link is personal: it opens the form in this person's name, brings back their previous answers, and cannot create a duplicate even if they spell their name differently. It only gives access to their own answers, never to the guest list.",
    "invitations.send": "Send the invitations",
    "invitations.sendCount": "Send to {n} guest(s)",
    "invitations.sendTitle": "Send {n} invitation(s)?",
    "invitations.sendHint":
      "Each guest with an address who has not answered receives their personal invitation, signed by you. Anyone still silent is reminded once after 7 days and once after 14, then left alone. At most three emails per person.",
    "invitations.sendConfirm": "Send",
    "invitations.cancel": "Cancel",
    "invitations.sent": "{n} invitation(s) sent",
    "invitations.sentPartial": "{n} sent, {f} failed",
    "invitations.sendFailed": "The invitations could not be sent",
    "invitations.needContact":
      "Add your email in the Overview tab first — replies to the invitation come back to you.",
    "invitations.notConfigured":
      "Sending is not set up on this server yet (missing mail key). You can still copy the links and send them yourself.",
    "invitations.preview": "Send me a preview",
    "invitations.previewSent": "Preview sent to {email}",
    "invitations.invitedOn": "invited {date}",
    "invitations.reminded": "{n} reminder(s)",
    "invitations.nextReminder": "next reminder {date}",
    "invitations.noMoreReminders": "no further reminders",
    "invitations.nothingToSend": "Everyone with an address has been invited.",
    "invitations.noAddressesYet": "No guest has an email address yet",
    "invitations.remindersOn": "Reminders run by themselves: 7 and 14 days after the invitation, to whoever has not answered.",
    "invitations.remindersSleep":
      "On the free hosting tier they go out when the server is awake — any visit to the site wakes it.",

    // --- plan board ----------------------------------------------------------------
    "plan.cards": "Cards",
    "plan.round": "Round tables",
    "plan.refine": "Refine",
    "plan.finish": "Finish",
    "plan.zoomOut": "Zoom out",
    "plan.zoomIn": "Zoom in",
    "plan.refineTitle": "Select the guests you are unsure about",
    "plan.refineHint":
      "You know things the form did not ask. Point out those people and the engine looks for where they would be better. The others stay put, and no proposal can break an avoid request.",
    "plan.picked": "{n} selected",
    "plan.computing": "Computing…",
    "plan.propose": "Propose moves",
    "plan.clear": "Clear",
    "plan.table": "table",
    "plan.swapWith": "swap with {names}",
    "plan.shares": "shares {things} with this table",
    "plan.move": "Move",
    "plan.dismiss": "Dismiss this proposal",
    "plan.alreadyBest": "These guests are already in the best place",
    "plan.alreadyBestDesc": "Nothing improves without making another table worse.",
    "plan.cannotCompute": "Could not compute moves",
    "plan.askedNotWith": "{name} asked not to be with {names}",
    "plan.neverBreak": "We never break an avoid request ourselves — it is your call.",
    "plan.placeAnyway": "Place anyway",

    // --- guest list panel / chips ---------------------------------------------------
    "list.guests": "Guests",
    "list.search": "Search a name or an interest",
    "list.all": "All",
    "list.noRsvp": "No RSVP",
    "list.conflicts": "Conflicts",
    "list.toDecide": "To decide",
    "list.noMatch": "No guest matches that filter.",
    "chip.notAnswered": "Has not answered the form",
    "chip.pinned": "Pinned to this table",
    "chip.conflict": "Avoid conflict",
    "chip.noRsvp": "No RSVP",
    "chip.move": "Move {name} to another table",

    // --- guest inspector -------------------------------------------------------------
    "inspector.title": "Guest card",
    "inspector.hint": "Click a guest to see their words, their diet and their requests.",
    "inspector.table": "Table {t}",
    "inspector.unseated": "not seated",
    "inspector.close": "Close guest details",
    "inspector.pinnedAt": "Pinned to table {t} — regenerating keeps this seat.",
    "inspector.freeToMove": "Regenerating may move this guest anywhere.",
    "inspector.unpin": "Unpin",
    "inspector.pinHere": "Pin here",
    "inspector.noAnswer": "Has not answered the form. Seated from the list: family, side and generation.",
    "inspector.ownWords": "In their words",
    "inspector.aiSummary": "AI summary",
    "inspector.interests": "Interests",
    "inspector.diet": "Diet",
    "inspector.comesWith": "Comes with",
    "inspector.wantsNear": "Wants to be near",
    "inspector.mustNot": "Must not be with",
    "inspector.circle": "Social circle",

    // --- validation panel -------------------------------------------------------------
    "validation.aria": "Live validation",
    "validation.allGood": "All in order",
    "validation.toCheck": "To check",
    "validation.allGoodHint": "Capacity, groups and avoid requests: everything is honoured.",
    "validation.toCheckHint": "Nothing is hidden: here is what is off.",
    "validation.seated": "Guests seated",
    "validation.avoidsHonoured": "Avoid requests honoured",
    "validation.wishes": "Sit-near wishes",
    "validation.overCapacity": "Tables over capacity",
    "validation.none": "none",
    "validation.splitParties": "Couples and families split",
    "validation.toDecide": "To decide",
    "validation.chemistry": "Average affinity",
    "validation.kept": "Kept on regeneration",
    "validation.tables": "{n} table(s)",
    "validation.guests": "{n} guest(s)",
    "validation.conflicts": "Avoid conflicts",
    "validation.fix": "fix",
    "validation.tablesWord": "table(s) {list}",
    "validation.parked": "{n} still in the tray",
    "validation.remarks": "Remarks",
    "validation.aiRead": "AI read the answers, grouped and wrote the notes",
    "validation.codeDid": "code did every calculation and the placement",
    "validation.generatedIn": "generated in {s} s",
    "validation.groups": "{n} groups",
    "validation.responses": "{p}% answered",

    // --- to-decide tray ----------------------------------------------------------------
    "tray.aria": "To decide",
    "tray.title": "To decide",
    "tray.hint": "The guests we refuse to seat badly — drop someone here to set them aside",
    "tray.empty": "Nobody waiting. Every guest is seated without breaking a single avoid request.",
    "tray.avoidReason": "No table fits without seating them next to {names} — your call.",
    "tray.someone": "someone to avoid",
    "tray.organiser": "Set aside by you.",
    "tray.noSeat": "No seat left.",

    // --- table card ---------------------------------------------------------------------
    "table.label": "TABLE {n}",
    "table.lockedTitle": "Locked — regenerating keeps this table as it is",
    "table.lockTitle": "Lock this table so regenerating keeps it",
    "table.children": "Children's table",
    "table.head": "Head table",
    "table.locked": "Locked",
    "table.over": "Over capacity",
    "table.conflicts": "{n} avoid conflict(s)",
    "table.staleNote": "note to refresh",
    "table.rename": "Click to rename",
    "table.aiNote": "AI note",
    "table.empty": "Empty table — drop guests here.",
    "table.kitchen": "Kitchen:",

    // --- move dialog ----------------------------------------------------------------------
    "move.title": "Move {name}",
    "move.currently": "Currently at table {t}.",
    "move.inTray": "Currently in the “to decide” tray.",
    "move.flagged": "Tables that would break an avoid request are flagged.",
    "move.wouldBeWith": "would be with {names}",
    "move.here": "here",
    "move.free": "{n} free",
    "move.wholeParty": "Move the whole party ({n} people{label}). Untick to split them.",
    "move.setAside": "Set aside",
    "move.cancel": "Cancel",

    // --- misc ---------------------------------------------------------------------------------
    "share.copied": "{label} copied",
    "timeline.finishedIn": "finished in",
    "timeline.elapsed": "elapsed",
    "timeline.ai": "AI",
    "timeline.code": "Code",
    "timeline.both": "AI + Code",

    // --- label maps -------------------------------------------------------------------------
    "role.couple": "The couple",
    "role.parent": "Parent",
    "role.witness": "Witness",
    "side.bride": "Bride's side",
    "side.groom": "Groom's side",
    "side.both": "Both sides",
    "age.child": "Child",
    "age.young_adult": "Young adult",
    "age.adult": "Adult",
    "age.middle_age": "Middle age",
    "age.senior": "Senior",
    "diet.vegetarian": "Vegetarian",
    "diet.vegan": "Vegan",
    "diet.pescatarian": "Pescatarian",
    "diet.gluten_free": "Gluten free",
    "diet.lactose_free": "Lactose free",
    "diet.nut_allergy": "Nut allergy",
    "diet.shellfish_allergy": "Shellfish allergy",
    "diet.halal": "Halal",
    "diet.kosher": "Kosher",
    "diet.no_pork": "No pork",
    "diet.no_alcohol": "No alcohol",
    "diet.low_salt": "Low salt",
    "diet.kids_menu": "Kids menu",
    "diet.other": "Special request",
    "dietShort.vegetarian": "veg",
    "dietShort.vegan": "vegan",
    "dietShort.pescatarian": "fish",
    "dietShort.gluten_free": "GF",
    "dietShort.lactose_free": "LF",
    "dietShort.nut_allergy": "nuts",
    "dietShort.shellfish_allergy": "shell",
    "dietShort.halal": "halal",
    "dietShort.kosher": "kosher",
    "dietShort.no_pork": "no pork",
    "dietShort.no_alcohol": "no alc",
    "dietShort.low_salt": "low salt",
    "dietShort.kids_menu": "kids",
    "dietShort.other": "note",
    "domain.outdoors_adventure": "Outdoors & adventure",
    "domain.endurance_sport": "Endurance sport",
    "domain.team_sport": "Team sport",
    "domain.motors_cars": "Motors & cars",
    "domain.water_sea": "Water & sea",
    "domain.performing_arts": "Performing arts",
    "domain.music_making": "Making music",
    "domain.music_listening": "Gigs & records",
    "domain.visual_arts": "Visual arts",
    "domain.books_writing": "Books & writing",
    "domain.film_series": "Film & series",
    "domain.food_cooking": "Food & cooking",
    "domain.wine_spirits": "Wine & spirits",
    "domain.travel": "Travel",
    "domain.tech_startups": "Tech & startups",
    "domain.science_research": "Science & research",
    "domain.medicine_care": "Medicine & care",
    "domain.teaching_kids": "Teaching & kids",
    "domain.business_finance": "Business & finance",
    "domain.crafts_making": "Crafts & making",
    "domain.garden_nature": "Garden & nature",
    "domain.animals_pets": "Animals & pets",
    "domain.games_puzzles": "Games & puzzles",
    "domain.wellness_yoga": "Wellness & yoga",
    "domain.community_faith": "Community & faith",
    "domain.family_local": "Family & local life",

    // --- the guest form --------------------------------------------------------------------------
    "rsvp.unavailable": "This form is not available",
    "rsvp.unavailableHint": "The link may be wrong, or the wedding has been removed.",
    "rsvp.home": "Back to home",
    "rsvp.invited": "You are invited",
    "rsvp.to": "to the wedding of",
    "rsvp.greeting": "Hello {name}.",
    "rsvp.alreadyAnswered":
      "You have already answered — your answers are below, change what you like and send again.",
    "rsvp.yourLink":
      "This link is yours: your answers go straight to your seat, with no risk of a namesake.",
    "rsvp.intro":
      "Two minutes, no more. The more you tell us in your own words, the better your table will be.",
    "rsvp.progress": "{done} of {total} answered",
    "rsvp.about": "About you",
    "rsvp.firstName": "First name",
    "rsvp.lastName": "Last name",
    "rsvp.age": "Age",
    "rsvp.profession": "Profession",
    "rsvp.professionPlaceholder": "Architect",
    "rsvp.passions": "One or two passions",
    "rsvp.passionsHint": "The fastest way to find you good neighbours at the table.",
    "rsvp.passionsPlaceholder": "Climbing, Italian cooking",
    "rsvp.company": "Who you are coming with",
    "rsvp.attendingWith": "Your partner, or the person you are coming with",
    "rsvp.attendingWithHint": "We never separate you.",
    "rsvp.attendingWithPlaceholder": "My husband Diego",
    "rsvp.alone": "I am coming alone",
    "rsvp.aloneValue": "Coming alone",
    "rsvp.childrenQuestion": "Are you bringing children?",
    "rsvp.childrenYes": "Yes",
    "rsvp.childrenNo": "No",
    "rsvp.childrenHint": "They will have their seat at the table, next to you.",
    "rsvp.childFirstName": "Child's first name",
    "rsvp.childLastName": "Last name",
    "rsvp.childAge": "Age",
    "rsvp.removeChild": "Remove child {n}",
    "rsvp.addChild": "Add a child",
    "rsvp.tellUs": "Tell us more",
    "rsvp.dietary": "A diet or an allergy?",
    "rsvp.dietaryPlaceholder": "Vegetarian, and no hazelnuts — a real allergy",
    "rsvp.dietaryHint": "Passed on as is to the caterer.",
    "rsvp.interests": "Tell us about yourself, in your own words",
    "rsvp.interestsPlaceholder":
      "I climb every weekend, mostly granite multi-pitch, and I took up trail running this winter",
    "rsvp.interestsHint": "This is what makes a good table. Do not hesitate to say too much.",
    "rsvp.seatNear": "Someone you would like to sit near?",
    "rsvp.seatNearPlaceholder": "Lena Roussel, and the climbing crowd",
    "rsvp.avoid": "Someone we should keep away from your table?",
    "rsvp.avoidPlaceholder": "Bernard Whitlock — long story, no drama",
    "rsvp.avoidHint": "Only the couple sees this. We will never seat you together.",
    "rsvp.required": "required",
    "rsvp.optional": "optional",
    "rsvp.yourData": "Your data",
    "rsvp.consentFallback":
      "I agree that this information is passed to the couple to organise the seating plan.",
    "rsvp.send": "Send my answers",
    "rsvp.onlyCouple": "Only the couple can read this",
    "rsvp.needFirstName": "Your first name lets us find you on the list",
    "rsvp.needLastName": "Your last name too — there may be two people with your first name",
    "rsvp.needCompany": "Tell us who you are coming with, or tick “I am coming alone”",
    "rsvp.needChildren": "Tell us whether you are bringing children",
    "rsvp.needChildName": "Each child needs at least a first name",
    "rsvp.needProfession": "Your profession helps us seat you well",
    "rsvp.needPassions": "One passion is enough — it is the best clue we have",
    "rsvp.needConsent": "Please confirm your agreement before sending",
    "rsvp.failed": "Could not send your answers",
    "rsvp.thanks": "Thank you, {name}.",
    "rsvp.arrived":
      "Your answers have reached {couple}. They will be used to find you a table with people you will enjoy — and the person you mentioned will not be seated with you.",
    "rsvp.childrenAdded": "{n} child(ren) added to your table.",
    "rsvp.matched": "We found you on the guest list.",
    "rsvp.addedToList": "You have been added to the guest list.",
    "rsvp.correctLink": "Need to change something? Open this same link again, your answers are waiting.",
    "rsvp.correctName": "Need to change something? Fill the form in again with the same name.",
    "rsvp.another": "Answer for someone else",
  },

  fr: {
    "create.back": "Retour",
    "create.step": "Première étape",
    "create.title": "Préparez la salle",
    "create.intro":
      "Les tables et les places d'abord. Vous recevrez un lien à partager avec vos invités ; leurs réponses viendront remplir la liste au fil de l'eau.",
    "create.couple": "Les mariés",
    "create.couplePlaceholder": "Amélie & Julien",
    "create.email": "Votre adresse mail",
    "create.emailHint":
      "C'est ici que reviennent les réponses à l'invitation. Placy en a besoin pour envoyer les invitations en votre nom.",
    "create.language": "Langue du mariage",
    "create.languageHint": "Les invitations et le plan imprimé sont rédigés dans cette langue.",
    "create.password": "Mot de passe",
    "create.passwordHint":
      "Protège votre liste d'invités — leurs régimes, et les personnes qu'ils ont demandé à éviter. Vos invités n'en ont jamais besoin : le lien du formulaire reste ouvert.",
    "create.passwordPlaceholder": "8 caractères minimum",
    "create.venue": "Lieu",
    "create.venuePlaceholder": "Domaine de la Roselière",
    "create.date": "Date",
    "create.pickDate": "Choisir une date",
    "create.tables": "Tables",
    "create.seats": "Places par table",
    "create.seatsWord": "places",
    "create.less": "Moins de",
    "create.more": "Plus de",
    "create.submit": "Créer le mariage",
    "create.orDemo": "ou essayez d'abord le mariage de démonstration",
    "create.whose": "C'est le mariage de qui ?",
    "create.shortPassword": "Choisissez un mot de passe d'au moins 8 caractères",
    "create.badEmail": "Cette adresse mail n'a pas l'air correcte",
    "create.created": "Mariage créé — invitez maintenant vos invités",
    "create.failed": "Impossible de créer le mariage",

    "nav.invitations": "Invitations",
    "admin.opening": "Ouverture de votre espace",
    "admin.private": "Ce mariage est privé",
    "admin.privateHint":
      "La liste contient des noms, des régimes et les demandes d'évitement. Saisissez le mot de passe pour y accéder.",
    "admin.passwordPlaceholder": "Mot de passe du mariage",
    "admin.open": "Ouvrir le plan",
    "admin.guestsNoNeed": "Vos invités n'en ont pas besoin : le lien du formulaire reste ouvert.",
    "admin.wrongPassword": "Mot de passe incorrect",
    "admin.notFound": "Nous ne trouvons pas ce mariage",
    "admin.notFoundHint": "Vérifiez le lien, ou créez-en un nouveau.",
    "admin.home": "Retour à l'accueil",
    "admin.demo": "mariage de démonstration",
    "admin.refreshNotes": "Actualiser {n} note(s) IA",
    "admin.working": "En cours",
    "admin.seating": "Placement de {n} invités",
    "admin.seatingHint":
      "Lecture des réponses, calcul des affinités, puis placement par le code. Généralement moins d'une minute.",
    "admin.noPlan": "Pas encore de plan",
    "admin.readyTitle": "Tout est prêt pour le placement",
    "admin.startTitle": "Commencez par ajouter vos invités",
    "admin.readyHint":
      "{guests} invités, {answered} réponses pour l'instant. Le plan fonctionne même si la plupart n'ont pas répondu.",
    "admin.startHint": "Ajoutez votre liste dans l'onglet Invités, puis partagez le lien du formulaire.",
    "admin.generateCta": "Générer le plan de table",
    "admin.manageGuests": "Gérer les invités",
    "admin.planReady": "Plan prêt en {s} s",
    "admin.planReadyDesc": "{seated} invités placés · {conflicts} conflits",
    "admin.generationFailed": "La génération a échoué",
    "admin.building": "Construction du plan",
    "admin.buildingDesc": "Généralement moins d'une minute.",
    "admin.cannotStart": "Impossible de lancer la génération",
    "admin.conflicts": "{n} conflit(s) d'évitement dans le plan",
    "admin.conflictsDesc": "Voir le panneau de vérification pour corriger.",
    "admin.overCapacity": "Table {t} en surcapacité",
    "admin.cannotMove": "Impossible de déplacer cet invité",
    "admin.locked": "Table {t} verrouillée — la régénération la conserve telle quelle",
    "admin.unlocked": "Table {t} déverrouillée",
    "admin.cannotLock": "Impossible de verrouiller cette table",
    "admin.pinned": "{name} reste à la table {t} à la régénération",
    "admin.unpinned": "{name} désépinglé·e",
    "admin.cannotPin": "Impossible d'épingler cet invité",
    "admin.renamed": "Table renommée « {title} »",
    "admin.nameRestored": "Nom généré rétabli",
    "admin.cannotRename": "Impossible de renommer cette table",
    "admin.notesRewritten": "{n} note(s) de table réécrite(s)",
    "admin.notesFresh": "Les notes sont déjà à jour",
    "admin.cannotRefresh": "Impossible d'actualiser les notes",
    "admin.guest": "Invité",
    "admin.rebuildRest": "Reconstruire le reste de la salle ?",
    "admin.startOver": "Repartir de zéro ?",
    "admin.movedByHand": "Vous avez déplacé des invités à la main.",
    "admin.keptLocked": "{n} table(s) verrouillée(s)",
    "admin.keptPinned": "{n} invité(s) épinglé(s)",
    "admin.and": "et",
    "admin.willBeKept":
      "seront conservés tels quels ; toutes les autres modifications manuelles seront perdues.",
    "admin.regenerateLoses":
      "Régénérer construit un plan entièrement nouveau et ces modifications manuelles seront perdues. Verrouillez une table ou épinglez un invité d'abord pour garder ce qui vous plaît.",
    "admin.keepPlan": "Garder mon plan",
    "admin.regenerate": "Régénérer",

    "overview.guestsInvited": "Invités",
    "overview.answeredForm": "Ont répondu au formulaire",
    "overview.ofList": "{p} % de la liste",
    "overview.seats": "Places",
    "overview.tablesTimes": "{n} tables × {s}",
    "overview.plan": "Plan",
    "overview.ready": "Prêt",
    "overview.notYet": "Pas encore",
    "overview.generateWhen": "Générez quand vous voulez",
    "overview.share": "Partager",
    "overview.twoLinks": "Deux liens, aucun compte",
    "overview.guestForm": "Formulaire invités (public)",
    "overview.guestFormHint": "À envoyer à vos invités. Deux minutes à remplir.",
    "overview.workspace": "Votre espace privé",
    "overview.workspaceHint": "Gardez celui-ci pour vous — quiconque l'a peut modifier le plan.",
    "overview.contact": "Votre adresse mail",
    "overview.contactHint":
      "C'est là qu'arrivent les réponses à l'invitation. Placy envoie les invitations en votre nom et en a besoin.",
    "overview.contactSave": "Enregistrer l'adresse",
    "overview.contactSaved": "Adresse enregistrée",
    "overview.invitation": "Invitation",
    "overview.invitationHint":
      "Rédigée pour vous, avec le lien dedans. Envoyez-la depuis Placy dans l'onglet Invitations, ou copiez-la et envoyez-la depuis votre propre messagerie.",
    "overview.subject": "Objet",
    "overview.copy": "Copier",
    "overview.copied": "Invitation copiée",
    "overview.openMail": "Ouvrir dans la messagerie",
    "overview.loadingMessage": "Chargement du message…",
    "overview.room": "La salle",
    "overview.tablesSeats": "Tables et places",
    "overview.tables": "Tables",
    "overview.seatsPerTable": "Places par table",
    "overview.seatsFor": "{total} places pour {n} invités",
    "overview.notEnough": " — pas assez de place",
    "overview.seatsEach": "Places à chaque table",
    "overview.seatsEachHint": "Une table d'honneur a rarement le même nombre de places que le reste de la salle.",
    "overview.table": "Table",
    "overview.headTable": "Table d'honneur",
    "overview.headTableHint":
      "Les invités marqués mariés, parent ou témoin dans l'onglet Invités y sont placés.",
    "overview.noHeadTable": "Pas de table d'honneur",
    "overview.kidsTable": "Table enfants",
    "overview.kidsHint":
      "Les enfants y sont placés à partir de l'âge choisi. En dessous, ils restent avec leurs parents — et le couple, lui, n'est jamais séparé.",
    "overview.noKidsTable": "Pas de table enfants",
    "overview.kidsFrom": "à partir de",
    "overview.kidsYears": "ans",
    "overview.kidsTitle": "En dessous de cet âge, l'enfant reste avec ses parents",
    "overview.feel": "L'ambiance de la salle",
    "overview.feelHint": "Appliqué à la génération. Les demandes d'évitement n'en dépendent jamais.",
    "overview.keepFamilies": "Garder les familles ensemble",
    "overview.mixFamilies": "Mélanger les familles",
    "overview.keepSides": "Garder chaque côté à part",
    "overview.mixSides": "Mélanger les deux côtés",
    "overview.saveRoom": "Enregistrer la salle",
    "overview.roomUpdated": "Salle mise à jour — régénérez pour replacer tout le monde",
    "overview.roomFailed": "Impossible d'enregistrer la salle",
    "overview.yourPlan": "Votre plan",
    "overview.nextStep": "Prochaine étape",
    "overview.seatedAcross": "{seated} invités placés sur {n} tables",
    "overview.everyAvoid": "Chaque demande d'évitement respectée",
    "overview.avoidToResolve": "{n} conflit(s) d'évitement à résoudre",
    "overview.wishesMet": "{a}/{b} souhaits de voisinage exaucés",
    "overview.toDecide": "{n} à décider",
    "overview.kitchen": "Résumé pour la cuisine",
    "overview.placementInCode": "placement par le code, {n} améliorations",
    "overview.generatedIn": "généré en {s} s",
    "overview.aiCalls": "{n} appels IA en {s} s",
    "overview.retried": "{n} réessayés",
    "overview.fellBack": "{n} repliés sur le code",
    "overview.embeddings": "embeddings",
    "overview.grouping": "regroupement",
    "overview.byAi": "par l'IA",
    "overview.byCode": "par le code",
    "overview.pinsKept": "{n} invités gardés là où vous les aviez fixés",
    "overview.openPlan": "Ouvrir le plan de table",
    "overview.exportPdf": "Exporter le PDF",
    "overview.readyWhen": "Quand vous voulez",
    "overview.addFirst":
      "Ajoutez d'abord votre liste d'invités, puis partagez le lien du formulaire. Vous pouvez générer un plan même si la plupart n'ont pas répondu.",
    "overview.partial":
      "{guests} invités sur la liste, {answered} ont répondu. Vous pouvez générer maintenant — le plan fonctionne avec des réponses partielles.",
    "overview.generatePlan": "Générer le plan de table",

    "guests.all": "Tous",
    "guests.answered": "Ont répondu",
    "guests.pending": "Sans réponse",
    "guests.imported": "{added} ajoutés, {updated} mis à jour",
    "guests.cannotRead": "Impossible de lire ce fichier",
    "guests.cannotApply": "Impossible d'appliquer ce changement",
    "guests.firstNameNeeded": "Il faut un prénom",
    "guests.added": "Invité ajouté",
    "guests.cannotAdd": "Impossible d'ajouter l'invité",
    "guests.updated": "Invité mis à jour",
    "guests.cannotUpdate": "Impossible de mettre à jour l'invité",
    "guests.removed": "{name} retiré·e",
    "guests.cannotRemove": "Impossible de retirer l'invité",
    "guests.spreadsheet": "Tableur",
    "guests.workFromExcel": "Travailler depuis Excel",
    "guests.excelHint":
      "Téléchargez la liste, modifiez-la dans Excel avec votre wedding planner, renvoyez-la. Les lignes gardent leur id, donc un envoi met les personnes à jour au lieu de les dupliquer — laissez l'id vide pour ajouter quelqu'un.",
    "guests.template": "Modèle vierge",
    "guests.export": "Exporter {n} invités",
    "guests.upload": "Envoyer une liste",
    "guests.importReport": "{added} ajoutés · {updated} mis à jour",
    "guests.children": "{n} enfants",
    "guests.inviteList": "Liste d'invités",
    "guests.addGuest": "Ajouter un invité",
    "guests.addHint":
      "Un foyer garde les gens ensemble : donnez le même nom de foyer à un couple ou à une famille. Un rôle place quelqu'un à la table d'honneur. Les invités remplissent le reste eux-mêmes sur",
    "guests.firstName": "Prénom",
    "guests.lastName": "Nom",
    "guests.email": "Courriel",
    "guests.emailHint": "pour son invitation personnelle",
    "guests.side": "Côté",
    "guests.household": "Foyer",
    "guests.householdPlaceholder": "marin",
    "guests.generation": "Génération",
    "guests.add": "Ajouter",
    "guests.count": "{n} invités",
    "guests.countAnswered": "({n} ont répondu)",
    "guests.search": "Chercher",
    "guests.selected": "{n} sélectionné(s)",
    "guests.setSide": "Définir le côté",
    "guests.setRole": "Définir le rôle",
    "guests.noRole": "Sans rôle",
    "guests.householdName": "Nom du foyer",
    "guests.delete": "Supprimer",
    "guests.clear": "Effacer",
    "guests.selectAll": "Tout sélectionner",
    "guests.shown": "{n} affichés",
    "guests.movedTo": "{n} passés côté {side}",
    "guests.backToOrdinary": "{n} redevenus invités ordinaires",
    "guests.markedAs": "{n} marqués {role}",
    "guests.groupedAs": "{n} regroupés sous « {name} »",
    "guests.ungrouped": "{n} dégroupés",
    "guests.householdLabel": "foyer",
    "guests.answeredBadge": "A répondu",
    "guests.noAnswer": "Sans réponse",
    "guests.edit": "Modifier {name}",
    "guests.remove": "Retirer {name}",
    "guests.select": "Sélectionner {name}",
    "guests.comingWith": "Vient avec",
    "guests.dietary": "Régime",
    "guests.ownWords": "Dans ses mots",
    "guests.wantsNear": "Souhaite être près de",
    "guests.mustNot": "Ne doit pas être avec",
    "guests.noMatch": "Aucun invité ne correspond à cette recherche.",
    "guests.none": "Aucun invité pour l'instant. Ajoutez le premier ci-dessus.",
    "guests.removeTitle": "Retirer {n} invité(s) ?",
    "guests.removeHint":
      "Leurs réponses partent avec eux — régimes, souhaits de voisinage, et les personnes qu'ils ont demandé à éviter. Les enfants qu'ils amenaient sont retirés aussi. C'est irréversible : exportez la liste d'abord si vous hésitez.",
    "guests.keepThem": "Les garder",
    "guests.removeConfirm": "Retirer",
    "guests.removedCount": "{n} retiré(s)",
    "guests.removedWithChildren": "{n} retiré(s), dont {c} enfants",

    "invitations.cannotLoad": "Impossible de lire les invitations",
    "invitations.noEmailSelection": "Aucun invité avec une adresse dans cette sélection.",
    "invitations.rowsCopied": "{n} ligne(s) copiée(s) — collez dans un tableur",
    "invitations.copyFailed": "Copie impossible depuis ce navigateur.",
    "invitations.loading": "Chargement…",
    "invitations.guests": "Invités",
    "invitations.withEmail": "Avec une adresse",
    "invitations.answered": "Ont répondu",
    "invitations.silent": "Sans réponse",
    "invitations.missingAddresses":
      "{n} invité(s) n'ont pas d'adresse, dont {m} qui n'ont pas encore répondu — vous ne pourrez pas les relancer par courriel. Ajoutez leur adresse dans le tableur (colonne « Courriel » du modèle Excel), ou transmettez-leur leur lien autrement.",
    "invitations.all": "Tous",
    "invitations.filterSilent": "Sans réponse",
    "invitations.filterAnswered": "Ont répondu",
    "invitations.noEmail": "Sans adresse",
    "invitations.search": "Chercher",
    "invitations.copySelection": "Copier la sélection",
    "invitations.hasAnswered": "A répondu",
    "invitations.notAnswered": "Sans réponse",
    "invitations.noAddress": "pas d'adresse",
    "invitations.openAsGuest": "Ouvrir le formulaire tel que l'invité le verra",
    "invitations.copyLink": "Copier son lien personnel",
    "invitations.nobody": "Personne dans cette sélection.",
    "invitations.personalHint":
      "Chaque lien est personnel : il ouvre le formulaire au nom de cette personne, ramène ses réponses précédentes, et ne peut pas créer de doublon même si elle écrit son nom autrement. Il ne donne accès qu'à ses propres réponses, jamais à la liste des invités.",
    "invitations.send": "Envoyer les invitations",
    "invitations.sendCount": "Envoyer à {n} invité(s)",
    "invitations.sendTitle": "Envoyer {n} invitation(s) ?",
    "invitations.sendHint":
      "Chaque invité qui a une adresse et n'a pas répondu reçoit son invitation personnelle, signée de vous. Ceux qui restent silencieux sont relancés une fois à 7 jours et une fois à 14, puis laissés tranquilles. Trois mails par personne au maximum.",
    "invitations.sendConfirm": "Envoyer",
    "invitations.cancel": "Annuler",
    "invitations.sent": "{n} invitation(s) envoyée(s)",
    "invitations.sentPartial": "{n} envoyée(s), {f} en échec",
    "invitations.sendFailed": "Les invitations n'ont pas pu partir",
    "invitations.needContact":
      "Renseignez d'abord votre adresse mail dans l'onglet Vue d'ensemble — les réponses à l'invitation vous reviennent.",
    "invitations.notConfigured":
      "L'envoi n'est pas encore configuré sur ce serveur (clé de messagerie absente). Vous pouvez toujours copier les liens et les envoyer vous-même.",
    "invitations.preview": "M'envoyer un aperçu",
    "invitations.previewSent": "Aperçu envoyé à {email}",
    "invitations.invitedOn": "invité·e le {date}",
    "invitations.reminded": "{n} relance(s)",
    "invitations.nextReminder": "prochaine relance le {date}",
    "invitations.noMoreReminders": "plus de relance",
    "invitations.nothingToSend": "Tous les invités qui ont une adresse ont été invités.",
    "invitations.noAddressesYet": "Aucun invité n'a encore d'adresse mail",
    "invitations.remindersOn":
      "Les relances partent toutes seules : 7 et 14 jours après l'invitation, à ceux qui n'ont pas répondu.",
    "invitations.remindersSleep":
      "Sur l'hébergement gratuit, elles partent quand le serveur est réveillé — n'importe quelle visite du site le réveille.",

    "plan.cards": "Cartes",
    "plan.round": "Tables rondes",
    "plan.refine": "Affiner",
    "plan.finish": "Terminer",
    "plan.zoomOut": "Dézoomer",
    "plan.zoomIn": "Zoomer",
    "plan.refineTitle": "Sélectionnez les invités dont vous doutez",
    "plan.refineHint":
      "Vous savez des choses que le formulaire n'a pas demandées. Désignez ces personnes et le moteur cherche où elles seraient mieux. Les autres ne bougent pas, et aucune proposition ne peut enfreindre une demande d'évitement.",
    "plan.picked": "{n} sélectionné(s)",
    "plan.computing": "Calcul…",
    "plan.propose": "Proposer des mouvements",
    "plan.clear": "Effacer",
    "plan.table": "table",
    "plan.swapWith": "échange avec {names}",
    "plan.shares": "partage {things} avec cette table",
    "plan.move": "Déplacer",
    "plan.dismiss": "Écarter cette proposition",
    "plan.alreadyBest": "Ces invités sont déjà à la meilleure place",
    "plan.alreadyBestDesc": "Rien ne s'améliore sans dégrader une autre table.",
    "plan.cannotCompute": "Impossible de calculer des mouvements",
    "plan.askedNotWith": "{name} a demandé à ne pas être avec {names}",
    "plan.neverBreak": "Nous n'enfreignons jamais un évitement de nous-mêmes — à vous de décider.",
    "plan.placeAnyway": "Placer quand même",

    "list.guests": "Invités",
    "list.search": "Chercher un nom ou un centre d'intérêt",
    "list.all": "Tous",
    "list.noRsvp": "Sans réponse",
    "list.conflicts": "Conflits",
    "list.toDecide": "À décider",
    "list.noMatch": "Aucun invité ne correspond à ce filtre.",
    "chip.notAnswered": "N'a pas répondu au formulaire",
    "chip.pinned": "Épinglé à cette table",
    "chip.conflict": "Conflit d'évitement",
    "chip.noRsvp": "Sans rép.",
    "chip.move": "Déplacer {name} vers une autre table",

    "inspector.title": "Fiche invité",
    "inspector.hint": "Cliquez sur un invité pour voir ses mots, son régime et ses demandes.",
    "inspector.table": "Table {t}",
    "inspector.unseated": "non placé",
    "inspector.close": "Fermer la fiche",
    "inspector.pinnedAt": "Épinglé à la table {t} — la régénération conserve cette place.",
    "inspector.freeToMove": "La régénération peut déplacer cet invité n'importe où.",
    "inspector.unpin": "Désépingler",
    "inspector.pinHere": "Épingler ici",
    "inspector.noAnswer": "N'a pas répondu au formulaire. Placé d'après la liste : famille, côté et génération.",
    "inspector.ownWords": "Dans ses mots",
    "inspector.aiSummary": "Résumé IA",
    "inspector.interests": "Centres d'intérêt",
    "inspector.diet": "Régime",
    "inspector.comesWith": "Vient avec",
    "inspector.wantsNear": "Souhaite être près de",
    "inspector.mustNot": "Ne doit pas être avec",
    "inspector.circle": "Cercle social",

    "validation.aria": "Vérification en direct",
    "validation.allGood": "Tout est en ordre",
    "validation.toCheck": "À vérifier",
    "validation.allGoodHint": "Capacité, groupes et demandes d'évitement : tout est respecté.",
    "validation.toCheckHint": "Rien n'est masqué : voici ce qui cloche.",
    "validation.seated": "Invités placés",
    "validation.avoidsHonoured": "Évitements respectés",
    "validation.wishes": "Souhaits de voisinage",
    "validation.overCapacity": "Tables en surcapacité",
    "validation.none": "aucune",
    "validation.splitParties": "Couples et familles séparés",
    "validation.toDecide": "À décider",
    "validation.chemistry": "Entente moyenne",
    "validation.kept": "Conservé à la régénération",
    "validation.tables": "{n} table(s)",
    "validation.guests": "{n} invité(s)",
    "validation.conflicts": "Conflits d'évitement",
    "validation.fix": "corriger",
    "validation.tablesWord": "table(s) {list}",
    "validation.parked": "{n} encore dans le bac",
    "validation.remarks": "Remarques",
    "validation.aiRead": "L'IA a lu les réponses, groupé et rédigé les notes",
    "validation.codeDid": "le code a fait tous les calculs et le placement",
    "validation.generatedIn": "généré en {s} s",
    "validation.groups": "{n} groupes",
    "validation.responses": "{p} % de réponses",

    "tray.aria": "À décider",
    "tray.title": "À décider",
    "tray.hint": "Les invités que nous refusons de mal placer — déposez quelqu'un ici pour le mettre de côté",
    "tray.empty": "Personne en attente. Tous les invités sont placés sans enfreindre une seule demande d'évitement.",
    "tray.avoidReason": "Aucune table ne convient sans le placer à côté de {names} — à vous de voir.",
    "tray.someone": "quelqu'un à éviter",
    "tray.organiser": "Mis de côté par vous.",
    "tray.noSeat": "Plus aucune place libre.",

    "table.label": "TABLE {n}",
    "table.lockedTitle": "Verrouillée — la régénération conserve cette table telle quelle",
    "table.lockTitle": "Verrouiller cette table pour que la régénération la conserve",
    "table.children": "Table enfants",
    "table.head": "Table d'honneur",
    "table.locked": "Verrouillée",
    "table.over": "Surcapacité",
    "table.conflicts": "{n} conflit(s) d'évitement",
    "table.staleNote": "note à actualiser",
    "table.rename": "Cliquer pour renommer",
    "table.aiNote": "Note IA",
    "table.empty": "Table vide — déposez des invités ici.",
    "table.kitchen": "Cuisine :",

    "move.title": "Déplacer {name}",
    "move.currently": "Actuellement à la table {t}.",
    "move.inTray": "Actuellement dans le bac « à décider ».",
    "move.flagged": "Les tables qui enfreindraient une demande d'évitement sont signalées.",
    "move.wouldBeWith": "serait avec {names}",
    "move.here": "ici",
    "move.free": "{n} libres",
    "move.wholeParty": "Déplacer tout le groupe ({n} personnes{label}). Décocher les sépare.",
    "move.setAside": "Mettre de côté",
    "move.cancel": "Annuler",

    "share.copied": "{label} copié",
    "timeline.finishedIn": "terminé en",
    "timeline.elapsed": "écoulé",
    "timeline.ai": "IA",
    "timeline.code": "Code",
    "timeline.both": "IA + Code",

    "role.couple": "Les mariés",
    "role.parent": "Parent",
    "role.witness": "Témoin",
    "side.bride": "Côté mariée",
    "side.groom": "Côté marié",
    "side.both": "Les deux",
    "age.child": "Enfant",
    "age.young_adult": "Jeune adulte",
    "age.adult": "Adulte",
    "age.middle_age": "Quinquagénaire",
    "age.senior": "Senior",
    "diet.vegetarian": "Végétarien",
    "diet.vegan": "Végane",
    "diet.pescatarian": "Pescétarien",
    "diet.gluten_free": "Sans gluten",
    "diet.lactose_free": "Sans lactose",
    "diet.nut_allergy": "Allergie aux fruits à coque",
    "diet.shellfish_allergy": "Allergie aux crustacés",
    "diet.halal": "Halal",
    "diet.kosher": "Casher",
    "diet.no_pork": "Sans porc",
    "diet.no_alcohol": "Sans alcool",
    "diet.low_salt": "Peu salé",
    "diet.kids_menu": "Menu enfant",
    "diet.other": "Demande particulière",
    "dietShort.vegetarian": "végé",
    "dietShort.vegan": "végane",
    "dietShort.pescatarian": "poisson",
    "dietShort.gluten_free": "s. gluten",
    "dietShort.lactose_free": "s. lactose",
    "dietShort.nut_allergy": "fruits à coque",
    "dietShort.shellfish_allergy": "crustacés",
    "dietShort.halal": "halal",
    "dietShort.kosher": "casher",
    "dietShort.no_pork": "s. porc",
    "dietShort.no_alcohol": "s. alcool",
    "dietShort.low_salt": "peu salé",
    "dietShort.kids_menu": "enfant",
    "dietShort.other": "note",
    "domain.outdoors_adventure": "Plein air & aventure",
    "domain.endurance_sport": "Sports d'endurance",
    "domain.team_sport": "Sports collectifs",
    "domain.motors_cars": "Moteurs & voitures",
    "domain.water_sea": "Eau & mer",
    "domain.performing_arts": "Spectacle vivant",
    "domain.music_making": "Pratique musicale",
    "domain.music_listening": "Concerts & disques",
    "domain.visual_arts": "Arts visuels",
    "domain.books_writing": "Livres & écriture",
    "domain.film_series": "Films & séries",
    "domain.food_cooking": "Cuisine & gastronomie",
    "domain.wine_spirits": "Vins & spiritueux",
    "domain.travel": "Voyages",
    "domain.tech_startups": "Tech & start-ups",
    "domain.science_research": "Sciences & recherche",
    "domain.medicine_care": "Médecine & soin",
    "domain.teaching_kids": "Enseignement & enfants",
    "domain.business_finance": "Affaires & finance",
    "domain.crafts_making": "Artisanat & bricolage",
    "domain.garden_nature": "Jardin & nature",
    "domain.animals_pets": "Animaux",
    "domain.games_puzzles": "Jeux & casse-têtes",
    "domain.wellness_yoga": "Bien-être & yoga",
    "domain.community_faith": "Engagement & spiritualité",
    "domain.family_local": "Famille & vie locale",

    "rsvp.unavailable": "Ce formulaire n'est pas disponible",
    "rsvp.unavailableHint": "Le lien est peut-être erroné, ou le mariage a été supprimé.",
    "rsvp.home": "Retour à l'accueil",
    "rsvp.invited": "Vous êtes invité·e",
    "rsvp.to": "au mariage de",
    "rsvp.greeting": "Bonjour {name}.",
    "rsvp.alreadyAnswered":
      "Vous avez déjà répondu — vos réponses sont ci-dessous, corrigez ce que vous voulez et renvoyez.",
    "rsvp.yourLink":
      "Ce lien est le vôtre : vos réponses iront directement sur votre place, sans risque d'homonyme.",
    "rsvp.intro":
      "Deux minutes, pas plus. Plus vous en dites avec vos propres mots, meilleure sera votre table.",
    "rsvp.progress": "{done} réponses sur {total}",
    "rsvp.about": "Vous",
    "rsvp.firstName": "Prénom",
    "rsvp.lastName": "Nom",
    "rsvp.age": "Âge",
    "rsvp.profession": "Métier",
    "rsvp.professionPlaceholder": "Architecte",
    "rsvp.passions": "Une ou deux passions",
    "rsvp.passionsHint": "Le raccourci le plus efficace pour vous trouver de bons voisins de table.",
    "rsvp.passionsPlaceholder": "Escalade, cuisine italienne",
    "rsvp.company": "Avec qui vous venez",
    "rsvp.attendingWith": "Votre conjoint·e, ou la personne qui vous accompagne",
    "rsvp.attendingWithHint": "Nous ne vous séparons jamais.",
    "rsvp.attendingWithPlaceholder": "Mon mari Diego",
    "rsvp.alone": "Je viens seul·e",
    "rsvp.aloneValue": "Vient seul·e",
    "rsvp.childrenQuestion": "Venez-vous avec des enfants ?",
    "rsvp.childrenYes": "Oui",
    "rsvp.childrenNo": "Non",
    "rsvp.childrenHint": "Ils auront leur place à table, à côté de vous.",
    "rsvp.childFirstName": "Prénom de l'enfant",
    "rsvp.childLastName": "Nom",
    "rsvp.childAge": "Âge",
    "rsvp.removeChild": "Retirer l'enfant {n}",
    "rsvp.addChild": "Ajouter un enfant",
    "rsvp.tellUs": "Dites-nous en plus",
    "rsvp.dietary": "Un régime ou une allergie ?",
    "rsvp.dietaryPlaceholder": "Végétarienne, et pas de noisette — c'est une vraie allergie",
    "rsvp.dietaryHint": "Transmis tel quel au traiteur.",
    "rsvp.interests": "Racontez-nous, avec vos mots",
    "rsvp.interestsPlaceholder":
      "Je grimpe tous les week-ends, surtout du granit en grande voie, et je me suis mise au trail cet hiver",
    "rsvp.interestsHint": "C'est ce qui fait une bonne table. N'hésitez pas à en dire trop.",
    "rsvp.seatNear": "Quelqu'un à côté de qui vous aimeriez être ?",
    "rsvp.seatNearPlaceholder": "Lena Roussel, et la bande de grimpeurs",
    "rsvp.avoid": "Quelqu'un que nous devrions éviter de placer à votre table ?",
    "rsvp.avoidPlaceholder": "Bernard Whitlock — longue histoire, sans drame",
    "rsvp.avoidHint": "Seuls les mariés le voient. Nous ne vous placerons jamais ensemble.",
    "rsvp.required": "obligatoire",
    "rsvp.optional": "facultatif",
    "rsvp.yourData": "Vos données",
    "rsvp.consentFallback":
      "J'accepte que ces informations soient transmises aux mariés pour l'organisation du plan de table.",
    "rsvp.send": "Envoyer mes réponses",
    "rsvp.onlyCouple": "Seuls les mariés peuvent lire ceci",
    "rsvp.needFirstName": "Votre prénom nous permet de vous retrouver sur la liste",
    "rsvp.needLastName": "Votre nom aussi — il peut y avoir deux personnes avec votre prénom",
    "rsvp.needCompany": "Dites-nous avec qui vous venez, ou cochez « Je viens seul·e »",
    "rsvp.needChildren": "Dites-nous si vous venez avec des enfants",
    "rsvp.needChildName": "Chaque enfant a besoin d'au moins un prénom",
    "rsvp.needProfession": "Votre métier nous aide à bien vous placer",
    "rsvp.needPassions": "Une passion suffit — c'est le meilleur indice que nous ayons",
    "rsvp.needConsent": "Merci de confirmer votre accord avant d'envoyer",
    "rsvp.failed": "Impossible d'envoyer vos réponses",
    "rsvp.thanks": "Merci, {name}.",
    "rsvp.arrived":
      "Vos réponses sont arrivées chez {couple}. Elles serviront à vous trouver une table avec des gens que vous aurez plaisir à côtoyer — et la personne que vous avez mentionnée ne sera pas placée avec vous.",
    "rsvp.childrenAdded": "{n} enfant(s) ajouté(s) à votre table.",
    "rsvp.matched": "Nous vous avons retrouvé·e sur la liste des invités.",
    "rsvp.addedToList": "Vous avez été ajouté·e à la liste des invités.",
    "rsvp.correctLink": "Une correction à apporter ? Rouvrez ce même lien, vos réponses vous attendent.",
    "rsvp.correctName": "Une correction à apporter ? Remplissez à nouveau le formulaire avec le même nom.",
    "rsvp.another": "Répondre pour quelqu'un d'autre",
  },
};
